import { useAuth0 } from '@auth0/auth0-react';
import posthog from 'posthog-js';
import { JSX, useEffect } from 'react';
import { useSelector } from 'react-redux';

import PageLoader from 'components/PageLoader';
import { showToast } from 'components/Toast';

import { useTheme } from 'context/Theme';

import UserRole from 'enums/UserRole';

import {
  useGetOrganizationAdminInfoLazyQuery,
  useGetStudentAccountInfoLazyQuery,
  useGetTeacherInfoLazyQuery,
} from 'graphql/types/generated';

import { useAppDispatch } from 'redux/store';
import {
  getUserId,
  getUserIsIdentified,
  getUserIsInfoLoaded,
  getUserRole,
  setUserIsInfoLoaded,
  setUserOrganizationId,
  setUserOrganizationName,
  setUserPlanTier,
  setUserSettings,
  setUserSubscriptionStatus,
} from 'redux/user';

import styles from './App.module.scss';

export interface AppLoaderProps {
  children: JSX.Element;
}

const AppLoader = ({ children }: AppLoaderProps) => {
  const { logout } = useAuth0();

  const { loading: isThemeLoading } = useTheme();

  const dispatch = useAppDispatch();

  const userId = useSelector(getUserId);
  const userRole = useSelector(getUserRole);
  const userIsIdentified = useSelector(getUserIsIdentified);
  const userIsInfoLoaded = useSelector(getUserIsInfoLoaded);

  const [getTeacherInfo, { data: teacherInfoData, error: teacherError }] =
    useGetTeacherInfoLazyQuery();
  const [
    getStudentAccountInfo,
    { data: studentAccountData, error: studentAccountError },
  ] = useGetStudentAccountInfoLazyQuery();
  const [
    getOrganizationAdminInfo,
    { data: organizationAdminData, error: organizationAdminError },
  ] = useGetOrganizationAdminInfoLazyQuery();

  const needsUserInfo =
    userRole === UserRole.Teacher ||
    userRole === UserRole.Student ||
    userRole === UserRole.OrganizationAdmin;
  const hasUserInfoError = !!(
    teacherError ??
    studentAccountError ??
    organizationAdminError
  );
  const isLoadingUserInfo =
    needsUserInfo && !userIsInfoLoaded && !hasUserInfoError;

  useEffect(() => {
    if (userRole === UserRole.Teacher) {
      getTeacherInfo({ variables: { teacherId: userId } });
    } else if (userRole === UserRole.Student) {
      getStudentAccountInfo({ variables: { studentAccountId: userId } });
    } else if (userRole === UserRole.OrganizationAdmin) {
      getOrganizationAdminInfo({ variables: { organizationAdminId: userId } });
    }
  }, [
    userId,
    userRole,
    getTeacherInfo,
    getStudentAccountInfo,
    getOrganizationAdminInfo,
  ]);

  useEffect(() => {
    if (teacherInfoData) {
      if (!teacherInfoData.teacher) {
        userIsIdentified && posthog.reset();
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
      if (teacherInfoData.teacher?.customer) {
        dispatch(setUserPlanTier(teacherInfoData.teacher.customer.planTier));
        dispatch(
          setUserSubscriptionStatus(
            teacherInfoData.teacher.customer.subscriptionStatus ?? null
          )
        );
      }
      if (teacherInfoData.teacher?.userSettings) {
        dispatch(setUserSettings(teacherInfoData.teacher.userSettings));
      }
      if (teacherInfoData.teacher?.organization) {
        dispatch(
          setUserOrganizationId(teacherInfoData.teacher.organization.id)
        );
        dispatch(
          setUserOrganizationName(teacherInfoData.teacher.organization.name)
        );
      }

      dispatch(setUserIsInfoLoaded(true));
    } else if (studentAccountData) {
      if (!studentAccountData.studentAccount) {
        userIsIdentified && posthog.reset();
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
      if (studentAccountData.studentAccount?.userSettings) {
        dispatch(
          setUserSettings(studentAccountData.studentAccount.userSettings)
        );
      }
      if (
        studentAccountData.studentAccount?.student.teachers[0]?.teacher
          .organization?.id
      ) {
        dispatch(
          setUserOrganizationId(
            studentAccountData.studentAccount?.student.teachers[0]?.teacher
              .organization?.id
          )
        );
      }
      dispatch(setUserIsInfoLoaded(true));
    } else if (organizationAdminData) {
      if (!organizationAdminData.organizationAdmin) {
        userIsIdentified && posthog.reset();
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
      if (organizationAdminData.organizationAdmin?.organization) {
        dispatch(
          setUserOrganizationId(
            organizationAdminData.organizationAdmin.organization.id
          )
        );
        dispatch(
          setUserOrganizationName(
            organizationAdminData.organizationAdmin.organization.name
          )
        );
      }
      dispatch(setUserIsInfoLoaded(true));
    }
  }, [
    teacherInfoData,
    studentAccountData,
    organizationAdminData,
    userIsIdentified,
    dispatch,
    logout,
  ]);

  useEffect(() => {
    (teacherError || studentAccountError || organizationAdminError) &&
      showToast(
        'userInfoError',
        'Error getting user info',
        'There was an issue retrieving your information. Try refreshing the page.',
        'error'
      );
  }, [teacherError, studentAccountError, organizationAdminError]);

  return isLoadingUserInfo || isThemeLoading ? (
    <PageLoader className={styles.LoadingIndicator} />
  ) : (
    children
  );
};

export default AppLoader;
